import type { ConfigurationPackage } from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import { assertNotEmpty, type Optional, typeUtils } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { FormikFileInput, FormikForm } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { SimpleModal } from '@festina-life/ui/structure'
import { useId, useMemo, useState } from 'react'
import * as Yup from 'yup'

import { DraftLocationSelector } from '../../../components/draftLocationSelectors/DraftLocationSelector'
import { useParameterDefinitionId } from '../../../hooks/useParameterDefinitionId'
import { useNavigationParams } from '../../../system/useNavigationParams'
import { useMutateConvertParameterValuesCsvToConfigurationPackage } from '../../../uiModels/api/parameters/useMutateConvertParameterValuesCsvToConfigurationPackage'
import { useApplicationViewOperationsContext } from '../../../workspace/applicationViewOperationsContext'
import { useRequiredBranchOperationsContext } from '../../../workspace/operationsContext'

type UploadCsvInput = {
  importFromFile: File[]
}

const validate = typeUtils.createValidator<UploadCsvInput>()

type Props = {
  onUpload: (params: {
    configurationPackage: Optional<ConfigurationPackage>
    layer: LayerKey
    block: string
  }) => void
  onClose: () => void
}

export const UploadValuesCsvFileToWorkspaceModal = ({
  onUpload,
  onClose,
}: Props) => {
  const translate = useTranslate()
  const formId = useId()
  const { mutateAsync, isPending, error } =
    useMutateConvertParameterValuesCsvToConfigurationPackage()
  const { branchKey } = useRequiredBranchOperationsContext()
  const parameterDefinitionId = useParameterDefinitionId()
  const { applicationParameterDefinitions } =
    useApplicationViewOperationsContext()
  const definitionLayer = applicationParameterDefinitions
    .get(parameterDefinitionId)
    ?.operations.at(0)?.layer
  const definitionBlock = applicationParameterDefinitions
    .get(parameterDefinitionId)
    ?.operations.at(0)?.block
  const { layer, block } = useNavigationParams()
  const [selectedLayer, setSelectedLayer] = useState<LayerKey>(layer)
  const [selectedBlock, setSelectedBlock] = useState<string>(block)
  assertNotEmpty(definitionLayer)

  const initialValues: UploadCsvInput = useMemo(
    () => ({
      importFromFile: [],
    }),
    [],
  )

  const validationSchema = Yup.object({
    importFromFile: Yup.array().required(),
  })

  const handleSubmit = async (value: UploadCsvInput) => {
    const csvFile = value.importFromFile.at(0)

    assertNotEmpty(definitionLayer)
    assertNotEmpty(definitionBlock)
    assertNotEmpty(csvFile)

    const configurationPackage = await mutateAsync({
      csvFile,
      server: branchKey.server,
      branch: branchKey.name,
      layer: definitionLayer,
      block: definitionBlock,
    })

    onUpload({
      configurationPackage,
      layer: selectedLayer,
      block: selectedBlock,
    })
  }

  return (
    <SimpleModal
      isOpen
      title={translate('uploadValuesCsvFileToWorkspace.modal.title')}
      closeConfiguration={{ onClose }}
      acceptConfiguration={{
        formId,
        loading: isPending,
        disabled: isPending,
        label: translate('uploadValuesCsvFileToWorkspace.modal.button.upload'),
      }}
      bodyOuterClassName="max-w-xl"
    >
      <Alert
        severity="warning"
        title={translate('uploadValuesCsvFileToWorkspace.modal.warning', {
          parameterDefinitionId,
        })}
      />
      {error && (
        <Alert
          severity="error"
          className="my-2"
          title={translate('uploadValuesCsvFileToWorkspace.modal.error', {
            error: error.message,
          })}
        />
      )}
      <FormikForm
        formId={formId}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        shouldSanitizeOnSubmit={false}
      >
        <DraftLocationSelector
          layer={selectedLayer}
          block={selectedBlock}
          onLayerChange={setSelectedLayer}
          onBlockChange={setSelectedBlock}
        />
        <FormikFileInput
          label={translate(
            'uploadValuesCsvFileToWorkspace.modal.fileSelector.label',
          )}
          description={translate(
            'uploadValuesCsvFileToWorkspace.modal.fileSelector.description',
          )}
          name={validate('importFromFile')}
          accept=".csv"
          multiple={false}
        />
      </FormikForm>
    </SimpleModal>
  )
}
